/**
 * Order-ticket prefill: a tiny external store the market page writes when the user clicks a book level,
 * and that the order ticket (#674) reads. No signing or order logic lives here.
 *
 * Convention: clicking an ask prefills a BUY at that price (lifting the offer); clicking a bid prefills a
 * SELL. The ticket may flip buy/sell itself (outcome rules: sells only up to shares held).
 * `seq` increments on every click so re-clicking the same level is still a new event.
 */
import { useSyncExternalStore } from 'react';
import type { Side } from '../hl/outcomes';

export interface TicketPrefill {
  outcome: number;
  side: Side;
  /** Raw wire price string from the book, e.g. "0.63215". */
  px: string;
  isBuy: boolean;
  seq: number;
}

type Listener = () => void;

let current: TicketPrefill | null = null;
let seq = 0;
const listeners = new Set<Listener>();

function emit(): void {
  for (const l of listeners) l();
}

export function setTicketPrefill(p: Omit<TicketPrefill, 'seq'>): TicketPrefill {
  current = { ...p, seq: ++seq };
  emit();
  return current;
}

export function clearTicketPrefill(): void {
  if (current === null) return;
  current = null;
  emit();
}

export function getTicketPrefill(): TicketPrefill | null {
  return current;
}

export function subscribeTicketPrefill(l: Listener): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

/** Current prefill (re-renders on change). */
export function useTicketPrefill(): TicketPrefill | null {
  return useSyncExternalStore(subscribeTicketPrefill, getTicketPrefill, getTicketPrefill);
}

/**
 * #873 "Close" on a portfolio position: the portfolio asks for a market sell of the whole free position, then opens
 * the market page. The ticket of that outcome takes the request once (`takeCloseRequest`) as soon as the own
 * balances are loaded, fills in Sell + Market + the free shares, and waits for the user to review and sign.
 * Nothing is ever submitted from here.
 */
export interface CloseRequest {
  outcome: number;
  side: Side;
  seq: number;
}

let close: CloseRequest | null = null;
let closeSeq = 0;

export function requestClose(p: Omit<CloseRequest, 'seq'>): CloseRequest {
  close = { ...p, seq: ++closeSeq };
  emit();
  return close;
}

export function getCloseRequest(): CloseRequest | null {
  return close;
}

/** Consume the pending request for `outcome` (null when there is none for it). */
export function takeCloseRequest(outcome: number): CloseRequest | null {
  if (close === null || close.outcome !== outcome) return null;
  const r = close;
  close = null;
  emit();
  return r;
}

/** Pending close request (re-renders on change). */
export function useCloseRequest(): CloseRequest | null {
  return useSyncExternalStore(subscribeTicketPrefill, getCloseRequest, getCloseRequest);
}
